import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { START } from 'shared/data/quoteSteps';
import { selectAssemblyParameters } from 'mx_pcb_assembly/store/selectors';
import { changeParameter, updateValidatorFlag } from 'mx_pcb_assembly/store/slice';
import { validator, projectValidator } from 'mx_pcb_assembly/store/validators';
import { doQuote } from 'mx_pcb_assembly/store/thunks';

import RequiredInformation from 'shared/components/RequiredInformation';
import EmailAddress from 'shared/components/parameters/EmailAddress';
import InstantQuote from 'shared/components/parameters/InstantQuote';
import GerberFile from 'shared/components/parameters/GerberFile';
import PartNumber from 'shared/components/parameters/PartNumber';
import Revision from 'shared/components/parameters/Revision';
import Quantities from 'shared/components/parameters/Quantities';
import BomLineItems from 'shared/components/parameters/BomLineItems';
import SMTPlacements from 'shared/components/parameters/SMTPlacements';
import THTPlacements from 'shared/components/parameters/THTPlacements';
import BGAPlacements from 'mx_pcb_assembly/components/BGAPlacements';
import QFNPlacements from 'shared/components/parameters/QFNPlacements';
import DoubleSidedSMT from 'shared/components/parameters/DoubleSidedSmt';
import DoubleSidedTHT from 'shared/components/parameters/DoubleSidedTHT';
import LeadFree from 'shared/components/parameters/LeadFree';
import ClassThreeInspection from 'shared/components/parameters/ClassThreeInspection';
import SpecialInstructions from 'shared/components/parameters/SpecialInstructions';

const Parameters = () => {
  const dispatch = useDispatch();
  const {
    parameters,
    loading,
    quoteStep,
    // eslint-disable-next-line no-unused-vars
    validatorFlag,
  } = useSelector(selectAssemblyParameters);

  const onChangeParameter = (name, value) => {
    dispatch(changeParameter({ name, value }));
  };

  const props = {
    parameters,
    onChangeParameter,
    loading,
    disabled: loading,
  };

  const onQuoteRequest = () => {
    if (validator.allValid()) {
      validator.showMessages();
      dispatch(doQuote());
    } else {
      validator.showMessages();
      dispatch(updateValidatorFlag());
    }
  };

  return (
    <div className="pcb-form-section">
      <div className="row">
        <div className="col-md-6">
          <GerberFile
            {...props}
            validator={projectValidator}
            errorMessage={projectValidator.message('Gerber File', parameters.filename, 'required')} />
        </div>
        <div className="col-md-6">
          <InstantQuote
            loading={loading}
            label={quoteStep !== START ? Spree.translations.requote : Spree.translations.quote}
            onQuoteRequest={onQuoteRequest} />
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <EmailAddress
            {...props}
            errorMessage={projectValidator.message('Email Address', parameters.email_address, 'required|email')} />
        </div>
      </div>
      <div className="row">
        <div className="col-md-6">
          <PartNumber
            {...props}
            errorMessage={projectValidator.message('Part Number', parameters.part_number, 'required|min:3')} />
        </div>
        <div className="col-md-6">
          <Revision
            {...props}
            errorMessage={projectValidator.message('Revision', parameters.revision, 'required')} />
        </div>
      </div>
      <div className="row">
        <div className="col-md-12">
          <Quantities
            {...props}
            errorMessage={validator.message('Quantity', parameters.quantities, 'group_required|group_numeric|group_min:1,num')} />
        </div>
        <div className="col-md-6">
          <BomLineItems
            {...props}
            errorMessage={validator.message('Bom Line Items', parameters.bom_line_items, 'required|numeric|min:0,num')} />
        </div>
        <div className="col-md-6" />
        <div className="col-md-6">
          <SMTPlacements
            {...props}
            errorMessage={validator.message('SMT Placements', parameters.smt, 'required|numeric|min:0,num')} />
        </div>
        <div className="col-md-6">
          <THTPlacements
            {...props}
            errorMessage={validator.message('Through Hole Placements', parameters.thru_hole, 'required|numeric|min:0,num')} />
        </div>
        <div className="col-md-6">
          <BGAPlacements {...props} />
        </div>
        <div className="col-md-6">
          <QFNPlacements
            {...props}
            errorMessage={validator.message('QFN Placements', parameters.qfn, 'required|numeric|min:0,num')} />
        </div>
        <div className="col-md-12">
          <LeadFree {...props} />
        </div>
        <div className="col-md-12">
          <DoubleSidedSMT {...props} />
        </div>
        <div className="col-md-12">
          <DoubleSidedTHT {...props} />
        </div>
        <div className="col-md-12">
          <ClassThreeInspection {...props} />
        </div>
        <div className="col-md-12">
          <SpecialInstructions
            {...props}
            errorMessage={validator.message('Special Instructions', parameters.special_instructions, 'max:255')} />
        </div>
      </div>
      <div className="row">
        <div className="col-md-12">
          <RequiredInformation />
        </div>
        <div className="col-md-6">
          <InstantQuote {...props} onQuoteRequest={onQuoteRequest} />
        </div>
      </div>
    </div>
  );
};

export default Parameters;
